import React, { useCallback } from 'react';
import { Box, ClickAwayListener } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import { useLayoutContext } from '../../context/LayoutContext';
import { LEFT_RAIL_WIDTH, LEFT_RAIL_WIDTH_HIDDEN, LeftRailProps } from '.';
import useDebounce from '../../../../lib/useDebounce';

type LayeredLeftRailProps = LeftRailProps;

const useLayeredStyles = makeStyles()(theme => ({
  root: {
    height: '100%',
    overflow: 'hidden',
    transition: theme.transitions.create(['width', 'visibility', 'transform'], {
      easing: 'cubic-bezier(0, 0, 0.2, 1)',
      duration: 500,
    }),
    visibility: 'hidden',
    width: LEFT_RAIL_WIDTH_HIDDEN,
    position: 'absolute',
    transform: `translateX(-${LEFT_RAIL_WIDTH}px)`,
    zIndex: theme.zIndex.drawer + 3,
  },
  open: {
    borderRight: `1px solid ${palette.gray_800}`,
    visibility: 'visible',
    width: LEFT_RAIL_WIDTH,
    transform: `translateX(0)`,
  },
}));

/**
 * @description A layout component for the Right Rail that implements an layered approach to displaying it in the page,
 *  i.e. this rail will layer on top of its siblings
 */
export default function LayeredLeftRail({ children }: LayeredLeftRailProps) {
  const { classes, cx } = useLayeredStyles();
  const { isLeftRailOpen, toggleLeftRail } = useLayoutContext();

  // Using a debounce to delay the update so that the rail doesn't close immediately after opening
  const debouncedIsLeftRailOpen = useDebounce(isLeftRailOpen, 500);

  const handleClickAway = useCallback(() => {
    if (debouncedIsLeftRailOpen) {
      toggleLeftRail(false);
    }
  }, [debouncedIsLeftRailOpen, toggleLeftRail]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        component="aside"
        aria-label="Main Menu"
        className={cx(classes.root, isLeftRailOpen && classes.open)}
      >
        {children}
      </Box>
    </ClickAwayListener>
  );
}
